/**
 * ONE TAP — hitscan combat, lag compensation, and damage resolution.
 *
 * Runs identically on the Cloudflare Worker and in the browser: no `three`, no DOM,
 * no Math.random().
 *
 * Two responsibilities:
 *   1. `traceShot` — a single honest bullet. World geometry and player hitboxes are
 *      tested against the same ray and the nearest surface wins, so a bullet can never
 *      pass through a wall to reach a player.
 *   2. `applyDamage` — the damage ladder from DESIGN.md §1: head kills outright, a body
 *      shot wounds, a second body shot kills. Wounds never regenerate; the only heal in
 *      the game is catching a chambered gun (see gun.ts).
 *
 * Lag compensation lives here too. `recordHistory` must be called exactly once per tick
 * by the world stepper, before inputs are applied; `traceShot` then rewinds every other
 * player to the shooter's view time before testing.
 */

import {
  BULLET_RANGE,
  HEAD_CROUCH_HEIGHT,
  HEAD_HEIGHT,
  HEAD_RADIUS,
  MAX_HEALTH,
  MAX_REWIND_MS,
  PLAYER_CROUCH_HEIGHT,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  SNAPSHOT_BUFFER_SIZE,
  THROW_INHERIT_VELOCITY,
  THROW_SPEED,
  THROW_UP_BIAS,
  WOUNDED_HEALTH,
  BODYSHOT_WOUNDS,
  HEADSHOT_LETHAL,
} from '@shared/constants';
import {
  GunState,
  HealthState,
  HitZone,
  SurfaceKind,
  type CollisionWorld,
  type GameEvent,
  type GunEntity,
  type PlayerState,
  type RaycastResult,
  type WorldState,
} from '@shared/types';
import {
  EPSILON,
  clamp,
  clamp01,
  clone,
  copy,
  addScaled,
  lengthSq,
  lerp,
  normalize,
  set,
  v3,
  type Vec3,
} from '@shared/math';

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

export interface ShotHit {
  playerId: string | null;
  zone: HitZone;
  point: Vec3;
  normal: Vec3;
  distance: number;
}

// ---------------------------------------------------------------------------
// Lag compensation history
//
// Keyed by WorldState in a WeakMap rather than kept in a module-level singleton:
// a single Worker isolate can host several GameRoom Durable Objects at once, and
// they must never share hitbox history.
// ---------------------------------------------------------------------------

/** ~1s of history at TICK_HZ — comfortably more than MAX_REWIND_MS. */
const HISTORY_FRAMES = SNAPSHOT_BUFFER_SIZE;

interface HistBody {
  pos: Vec3;
  crouching: boolean;
}

interface HistFrame {
  tick: number;
  timeMs: number;
  bodies: Map<string, HistBody>;
}

interface History {
  frames: (HistFrame | null)[];
  /** Index the next frame will be written to. */
  head: number;
  count: number;
}

const histories = new WeakMap<WorldState, History>();

/**
 * Snapshot every player's hitbox anchor for this tick. The world stepper calls this
 * once per tick. Without it, `traceShot` silently degrades to no rewind (still correct,
 * just less forgiving to high-ping shooters).
 */
export function recordHistory(w: WorldState): void {
  let h = histories.get(w);
  if (!h) {
    h = { frames: new Array<HistFrame | null>(HISTORY_FRAMES).fill(null), head: 0, count: 0 };
    histories.set(w, h);
  }

  // A round restart (or a client resyncing to an older server time) makes the buffer
  // non-monotonic, which would produce nonsense interpolation. Drop it and start over.
  const newest = frameAt(h, 0);
  if (newest !== null && w.timeMs < newest.timeMs) {
    h.head = 0;
    h.count = 0;
  }

  let f = h.frames[h.head];
  if (!f) {
    f = { tick: 0, timeMs: 0, bodies: new Map<string, HistBody>() };
    h.frames[h.head] = f;
  }
  f.tick = w.tick;
  f.timeMs = w.timeMs;
  f.bodies.clear();
  for (const id in w.players) {
    const p = w.players[id];
    if (!p) continue;
    f.bodies.set(id, { pos: clone(p.pos), crouching: p.crouching });
  }

  h.head = (h.head + 1) % HISTORY_FRAMES;
  if (h.count < HISTORY_FRAMES) h.count++;
}

/** Forget the rewind buffer for a world (round reset, world teardown). Safe to call any time. */
export function resetHistory(w: WorldState): void {
  histories.delete(w);
}

/** `back` counts backwards from the newest frame: 0 = newest. */
function frameAt(h: History, back: number): HistFrame | null {
  if (back < 0 || back >= h.count) return null;
  const i = (h.head - 1 - back + HISTORY_FRAMES * 2) % HISTORY_FRAMES;
  return h.frames[i] ?? null;
}

/**
 * Fill `out` with a player's position at `targetMs`, interpolating between the two
 * bracketing frames. Returns the crouch state at that time, or null when no history
 * exists for this player (caller should fall back to the live position).
 */
function sampleBody(h: History, targetMs: number, id: string, out: Vec3): boolean | null {
  const newest = frameAt(h, 0);
  if (!newest) return null;

  if (targetMs >= newest.timeMs) {
    const b = newest.bodies.get(id);
    if (!b) return null;
    copy(out, b.pos);
    return b.crouching;
  }

  for (let i = 1; i < h.count; i++) {
    const older = frameAt(h, i);
    if (!older) break;
    if (older.timeMs > targetMs) continue;

    const newer = frameAt(h, i - 1);
    const b0 = older.bodies.get(id);
    const b1 = newer ? newer.bodies.get(id) : undefined;
    if (!b0 || !b1 || !newer) {
      const b = b0 ?? b1;
      if (!b) return null;
      copy(out, b.pos);
      return b.crouching;
    }
    const span = newer.timeMs - older.timeMs;
    const a = span > EPSILON ? clamp01((targetMs - older.timeMs) / span) : 0;
    lerp(out, b0.pos, b1.pos, a);
    return a < 0.5 ? b0.crouching : b1.crouching;
  }

  // Older than anything we kept — clamp to the oldest frame we have.
  const oldest = frameAt(h, h.count - 1);
  const b = oldest ? oldest.bodies.get(id) : undefined;
  if (!b) return null;
  copy(out, b.pos);
  return b.crouching;
}

// ---------------------------------------------------------------------------
// Ray / hitbox primitives
// ---------------------------------------------------------------------------

/**
 * Ray vs sphere. `rd` must be unit length. Returns the entry distance, 0 when the ray
 * starts inside (point-blank), or -1 for a miss.
 */
function raySphere(
  ro: Vec3,
  rd: Vec3,
  cx: number,
  cy: number,
  cz: number,
  r: number,
): number {
  const ox = ro.x - cx;
  const oy = ro.y - cy;
  const oz = ro.z - cz;
  const c = ox * ox + oy * oy + oz * oz - r * r;
  if (c <= 0) return 0; // origin inside the sphere
  const b = ox * rd.x + oy * rd.y + oz * rd.z;
  if (b > 0) return -1; // sphere is entirely behind the ray
  const disc = b * b - c;
  if (disc < 0) return -1;
  const t = -b - Math.sqrt(disc);
  return t >= 0 ? t : -1;
}

/**
 * Ray vs vertical capsule (the player body). The capsule is the segment
 * (cx, yLo, cz) -> (cx, yHi, cz) swept by `r`. `rd` must be unit length.
 * Returns the entry distance, 0 when the origin is inside, or -1 for a miss.
 */
function rayCapsuleY(
  ro: Vec3,
  rd: Vec3,
  cx: number,
  cz: number,
  yLo: number,
  yHi: number,
  r: number,
): number {
  const dx = ro.x - cx;
  const dz = ro.z - cz;

  // Origin inside?
  const clampedY = clamp(ro.y, yLo, yHi);
  const oy = ro.y - clampedY;
  if (dx * dx + dz * dz + oy * oy <= r * r) return 0;

  let best = Number.POSITIVE_INFINITY;

  // Infinite cylinder, restricted to the segment's y-range.
  const a = rd.x * rd.x + rd.z * rd.z;
  if (a > EPSILON) {
    const b = 2 * (dx * rd.x + dz * rd.z);
    const c = dx * dx + dz * dz - r * r;
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      const inv = 1 / (2 * a);
      const t0 = (-b - sq) * inv;
      if (t0 >= 0) {
        const y = ro.y + rd.y * t0;
        if (y >= yLo && y <= yHi) best = t0;
      }
      if (best === Number.POSITIVE_INFINITY) {
        const t1 = (-b + sq) * inv;
        if (t1 >= 0) {
          const y = ro.y + rd.y * t1;
          if (y >= yLo && y <= yHi) best = t1;
        }
      }
    }
  }

  // End caps.
  const tLo = raySphere(ro, rd, cx, yLo, cz, r);
  if (tLo >= 0 && tLo < best) best = tLo;
  const tHi = raySphere(ro, rd, cx, yHi, cz, r);
  if (tHi >= 0 && tHi < best) best = tHi;

  return best === Number.POSITIVE_INFINITY ? -1 : best;
}

// ---------------------------------------------------------------------------
// traceShot
// ---------------------------------------------------------------------------

const _dir = v3();
const _bodyPos = v3();
const _rc: RaycastResult = {
  hit: false,
  distance: 0,
  point: v3(),
  normal: v3(),
  surfaceId: -1,
  surface: SurfaceKind.Concrete,
};

/**
 * One bullet. Tests world geometry and every other living player's hitboxes against the
 * same ray; the nearest intersection wins, so geometry always occludes players.
 *
 * `rewindMs` is how far back in time to move the *other* players before testing — the
 * caller derives it from the shooter's latency / view time. It is clamped to
 * MAX_REWIND_MS here so no caller can hand out an unbounded advantage.
 *
 * The head is a sphere and takes priority wherever it overlaps the body capsule, because
 * a headshot is an instant kill and players aim for heads on purpose.
 */
export function traceShot(
  origin: Vec3,
  dir: Vec3,
  shooterId: string,
  w: WorldState,
  world: CollisionWorld,
  rewindMs: number,
): ShotHit {
  const d = normalize(_dir, dir);

  const res: ShotHit = {
    playerId: null,
    zone: HitZone.None,
    point: v3(origin.x, origin.y, origin.z),
    normal: v3(-d.x, -d.y, -d.z),
    distance: 0,
  };

  // Degenerate aim direction: report a zero-length miss rather than NaNs downstream.
  if (lengthSq(d) < 0.5) return res;

  // --- world geometry first; it sets the far bound for player tests -------------
  let bestT = BULLET_RANGE;
  const rc = world.raycast(origin, d, BULLET_RANGE, _rc);
  if (rc.hit) {
    bestT = rc.distance;
    copy(res.point, rc.point);
    copy(res.normal, rc.normal);
    res.distance = rc.distance;
  } else {
    addScaled(res.point, origin, d, BULLET_RANGE);
    res.distance = BULLET_RANGE;
  }

  // --- players, rewound to the shooter's view time -------------------------------
  const rw = clamp(rewindMs, 0, MAX_REWIND_MS);
  const hist = rw > EPSILON ? histories.get(w) : undefined;
  const targetMs = w.timeMs - rw;

  for (const id in w.players) {
    if (id === shooterId) continue;
    const p = w.players[id];
    if (!p) continue;
    if (p.healthState === HealthState.Dead) continue;

    copy(_bodyPos, p.pos);
    let crouch = p.crouching;
    if (hist) {
      const c = sampleBody(hist, targetMs, id, _bodyPos);
      if (c === null) copy(_bodyPos, p.pos);
      else crouch = c;
    }

    const headY = _bodyPos.y + (crouch ? HEAD_CROUCH_HEIGHT : HEAD_HEIGHT);
    const bodyTop = _bodyPos.y + (crouch ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT);

    let t = raySphere(origin, d, _bodyPos.x, headY, _bodyPos.z, HEAD_RADIUS);
    let zone: HitZone = HitZone.Head;
    if (t < 0) {
      const yLo = _bodyPos.y + PLAYER_RADIUS;
      const yHi = Math.max(yLo, bodyTop - PLAYER_RADIUS);
      t = rayCapsuleY(origin, d, _bodyPos.x, _bodyPos.z, yLo, yHi, PLAYER_RADIUS);
      zone = HitZone.Body;
    }
    if (t < 0 || t >= bestT) continue;

    bestT = t;
    res.playerId = id;
    res.zone = zone;
    res.distance = t;
    addScaled(res.point, origin, d, t);

    if (zone === HitZone.Head) {
      set(res.normal, res.point.x - _bodyPos.x, res.point.y - headY, res.point.z - _bodyPos.z);
    } else {
      const yLo = _bodyPos.y + PLAYER_RADIUS;
      const yHi = Math.max(yLo, bodyTop - PLAYER_RADIUS);
      const cy = clamp(res.point.y, yLo, yHi);
      set(res.normal, res.point.x - _bodyPos.x, res.point.y - cy, res.point.z - _bodyPos.z);
    }
    if (lengthSq(res.normal) < EPSILON) set(res.normal, -d.x, -d.y, -d.z);
    else normalize(res.normal, res.normal);
  }

  return res;
}

// ---------------------------------------------------------------------------
// Damage
// ---------------------------------------------------------------------------

function gunOf(p: PlayerState, w: WorldState): GunEntity | null {
  if (p.heldGun < 0) return null;
  const direct = w.guns[p.heldGun];
  if (direct && direct.id === p.heldGun) return direct;
  for (let i = 0; i < w.guns.length; i++) {
    const g = w.guns[i];
    if (g && g.id === p.heldGun) return g;
  }
  return null;
}

/**
 * A dying player throws their gun clear. It keeps their momentum and pops upward, so it
 * stays catchable for a beat — a teammate can convert a death into a chambered round.
 * The gun must NEVER vanish with the corpse; that would deadlock the round.
 *
 * The toss is composed from existing throw constants (inherit fraction + up bias applied
 * to the base throw speed) rather than a new tunable.
 */
function dropGunOnDeath(victim: PlayerState, w: WorldState): void {
  const g = gunOf(victim, w);
  victim.heldGun = -1;
  if (!g) return;

  g.state = GunState.InFlight;
  g.holder = null;
  g.lastThrower = victim.id;
  g.throwLockoutMs = 0; // the corpse can't catch it anyway; let everyone else contest it
  g.idleMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = { x: g.pos.x, y: g.pos.y, z: g.pos.z };
  g.vel.x = victim.vel.x * THROW_INHERIT_VELOCITY;
  g.vel.y = victim.vel.y * THROW_INHERIT_VELOCITY + THROW_SPEED * THROW_UP_BIAS;
  g.vel.z = victim.vel.z * THROW_INHERIT_VELOCITY;
  g.spin.x = 0;
  g.spin.y = THROW_SPEED * THROW_UP_BIAS;
  g.spin.z = 0;
}

function killPlayer(
  victim: PlayerState,
  attacker: PlayerState | undefined,
  attackerId: string,
  zone: HitZone,
  gunId: number,
  w: WorldState,
  events: GameEvent[],
): void {
  victim.healthState = HealthState.Dead;
  victim.health = 0;
  victim.deaths++;
  victim.crouching = false;
  victim.throwChargeMs = 0;
  dropGunOnDeath(victim, w);
  if (attacker && attacker.id !== victim.id) attacker.kills++;
  events.push({ t: 'kill', killer: attackerId, victim: victim.id, zone, gunId });
}

/**
 * The damage ladder (DESIGN.md §1):
 *   head  -> instant kill, any range, no exceptions.
 *   body  -> Healthy becomes Wounded; Wounded becomes Dead.
 * Wounds do not regenerate. Friendly fire is off: a shot that lands on a teammate is
 * absorbed silently (the round is still spent — that is the punishment).
 */
export function applyDamage(
  victimId: string,
  attackerId: string,
  zone: HitZone,
  gunId: number,
  w: WorldState,
  events: GameEvent[],
): void {
  if (zone === HitZone.None) return;

  const victim = w.players[victimId];
  if (!victim) return;
  if (victim.healthState === HealthState.Dead) return;

  const attacker = w.players[attackerId];
  const isSelf = attackerId === victimId;
  // Friendly fire OFF. Absorb it cleanly — no damage, no event, no crash.
  if (!isSelf && attacker && attacker.team === victim.team) return;

  const lethalHead = zone === HitZone.Head && HEADSHOT_LETHAL;
  if (lethalHead) {
    killPlayer(victim, attacker, attackerId, HitZone.Head, gunId, w, events);
    return;
  }

  if (!BODYSHOT_WOUNDS) {
    killPlayer(victim, attacker, attackerId, zone, gunId, w, events);
    return;
  }

  if (victim.healthState === HealthState.Healthy) {
    victim.healthState = HealthState.Wounded;
    victim.health = WOUNDED_HEALTH;
    events.push({ t: 'wound', victim: victim.id, attacker: attackerId });
    return;
  }

  // Already wounded: the second body shot finishes it.
  killPlayer(victim, attacker, attackerId, zone, gunId, w, events);
}

/**
 * Reset a player's vitals to full health. Used by the catch-heal in gun.ts and by round
 * setup in match.ts, so the "what does full health mean" rule lives in exactly one place.
 */
export function reviveToFull(p: PlayerState): void {
  p.healthState = HealthState.Healthy;
  p.health = MAX_HEALTH;
}
